import React, { useState, useCallback } from "react";
import { Button, Spinner } from "reactstrap";

/**
 * Botão com estado de carregamento.
 *
 * Ao clicar, o botão fica desabilitado e exibe um spinner centralizado no lugar
 * do conteúdo, mantendo a mesma largura/altura (sem "pulo" de layout). Isso
 * evita cliques repetidos enquanto a primeira solicitação está em andamento.
 *
 * Dois modos de uso:
 *
 * 1. Controlado — passe `loading` (boolean). O componente pai liga/desliga o
 *    estado em volta da operação. Use em botões `type="submit"` de formulários
 *    e em fluxos com confirmação (sweetalert), onde o carregamento começa só
 *    depois de o usuário confirmar.
 *
 *      const [submitting, setSubmitting] = useState(false);
 *      <LoadingButton type="submit" color="success" loading={submitting}>
 *        Cadastrar
 *      </LoadingButton>
 *
 * 2. Automático — não passe `loading`. Se o `onClick` retornar uma Promise, o
 *    botão liga o spinner sozinho e dá `await` até resolver/rejeitar. Use em
 *    botões `onClick` assíncronos simples (lembre de `return` na Promise).
 *
 *      <LoadingButton color="success" onClick={() => SomeService.save(...)}>
 *        Salvar
 *      </LoadingButton>
 *
 * Demais props (`color`, `type`, `className`, `style`, `size`, etc.) são
 * repassadas ao Button do reactstrap — é um substituto direto.
 */
const LoadingButton = ({
  loading: loadingProp,
  onClick,
  disabled,
  children,
  style,
  spinnerSize = "sm",
  spinnerProps,
  ...rest
}) => {
  const [autoLoading, setAutoLoading] = useState(false);

  const isControlled = loadingProp !== undefined;
  const loading = isControlled ? loadingProp : autoLoading;

  const handleClick = useCallback(
    (event) => {
      // Guarda contra duplo-clique enquanto a requisição está em andamento.
      if (loading || !onClick) return;

      // Modo controlado: o pai cuida do estado de loading (inclusive em
      // botões type="submit", onde a submissão do form segue normalmente).
      if (isControlled) {
        onClick(event);
        return;
      }

      // Modo automático: se o handler retornar uma Promise, gerencia o loading.
      const result = onClick(event);
      if (result && typeof result.then === "function") {
        setAutoLoading(true);
        Promise.resolve(result).finally(() => setAutoLoading(false));
      }
    },
    [loading, onClick, isControlled]
  );

  return (
    <Button
      {...rest}
      onClick={handleClick}
      disabled={disabled || loading}
      style={{ position: "relative", ...style }}
    >
      {loading ? (
        <>
          {/* Conteúdo original escondido preserva a largura/altura do botão. */}
          <span style={{ visibility: "hidden", display: "inline-flex" }}>
            {children}
          </span>
          <span
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {/* Sem `color` o spinner herda a cor do texto do botão (branco em
                botões sólidos como success/primary). */}
            <Spinner size={spinnerSize} {...spinnerProps} />
          </span>
        </>
      ) : (
        children
      )}
    </Button>
  );
};

export default LoadingButton;
